/* drawlz-native-pdp.js — fixes for NATIVE Color x Size products (Round 24)
 *
 * Scope: products whose color is a VARIANT OPTION (Blessed Tee, SuedeSkin
 * Crew), not sibling-colorway products. Sibling-colorway PDPs (the underwear
 * lines, Expressionz) and single-color products EXIT before doing anything —
 * see the three guards at the top of run(). Nothing here can regress them.
 *
 * Why this file exists instead of an edit to drawlz-pdp.js: that file is
 * 74 KB and themeFilesUpsert is whole-file-only, so editing it means
 * re-emitting every byte by hand — the documented corruption mode. This is
 * the same override-layer trick drawlz-sitewide.css uses for CSS, applied
 * to JS: load LAST, take over one narrow behavior, leave the big file alone.
 *
 * WHAT IT FIXES
 *
 * 1. Gallery colour filter. drawlz-pdp.js already has a filter (Round 20)
 *    but it buckets media by ALT TEXT. On Blessed Tee the White variant's
 *    image is described as "men's black graphic t-shirt", so White matched
 *    nothing, the all-false fallback fired, and every colour showed every
 *    image. This replaces alt-text guessing with the actual variant->image
 *    association, which is authoritative and survives any copy rewrite.
 *
 * 2. Sticky bar thumbnail. The sticky thumb had no data hook at all, so
 *    nothing could update it and it kept showing the featured (black)
 *    image. drawlz-pdp-sticky-cta.liquid now carries
 *    data-drawlz-sticky-thumb; this syncs it on colour change.
 *
 * HOW IT TAKES OVER THE GALLERY
 * The built-in gallery binds to [data-drawlz-gallery]. This script is loaded
 * as a CLASSIC script from content_for_footer.liquid, so it executes during
 * parsing — before the DEFERRED drawlz-pdp.js runs. It renames the attribute
 * to data-drawlz-native-gallery, so initGallery() finds nothing and never
 * binds, and no listener is ever double-bound. If drawlz-pdp.js somehow
 * booted first, the claim is skipped entirely and stock behaviour stands.
 *
 * Colour state stays owned by drawlz-pdp.js — this file never writes it. It
 * READS the selected colour off aria-pressed and watches it with a
 * MutationObserver, so it picks up swatch clicks, the boot default, the
 * size-aware invalidation path and the second-close mirror clone for free.
 */
(function () {
  'use strict';

  var d = document;

  /* basename of a CDN url. A variant's image and a gallery slide's <img src>
     are rendered from the SAME uploaded file at different widths
     (?width=200 vs ?width=900), so the filename is the shared identity. */
  function baseName(u) {
    if (!u) return null;
    var s = String(u).split('?')[0].split('#')[0];
    var seg = s.split('/');
    return seg[seg.length - 1] || null;
  }

  function onReady(fn) {
    if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  function run() {
    /* ---- guard 1: the variant map must exist (drawlz-size-selector) ---- */
    var mapEl = d.querySelector('[data-drawlz-variant-map]');
    if (!mapEl) return;
    var vmap = null;
    try { vmap = JSON.parse(mapEl.textContent); } catch (err) { return; }
    if (!vmap || !vmap.variants || !vmap.variants.length) return;

    /* ---- guard 2: the product must HAVE a native Color option ---- */
    if (typeof vmap.colorIndex !== 'number' || vmap.colorIndex < 0) return;

    /* colour -> its own media (basenames), first full url, first price */
    var colors = [];
    var colorImg = {};
    var colorSrc = {};
    var colorPrice = {};
    vmap.variants.forEach(function (v) {
      var c = v.opts && v.opts[vmap.colorIndex];
      if (!c) return;
      if (colors.indexOf(c) === -1) { colors.push(c); colorImg[c] = {}; }
      var b = baseName(v.img);
      if (b) {
        colorImg[c][b] = true;
        if (!colorSrc[c]) colorSrc[c] = v.img;
      }
      if (colorPrice[c] == null && v.pf) colorPrice[c] = v.pf;
    });

    /* ---- guard 3: more than one colour, same test the swatch snippet
           uses. One colour means there is nothing to filter BY. ---- */
    if (colors.length < 2) return;

    /* claim the gallery before the deferred drawlz-pdp.js can bind to it */
    var roots = [];
    if (!window.__drawlzPdpBooted) {
      Array.prototype.forEach.call(d.querySelectorAll('[data-drawlz-gallery]'), function (root) {
        root.removeAttribute('data-drawlz-gallery');
        root.setAttribute('data-drawlz-native-gallery', '');
        roots.push(root);
      });
    }

    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var scrollBehavior = reduced ? 'auto' : 'smooth';
    var applyToGalleries = [];

    /* ---------- gallery (faithful port of initGallery + new filter) ---------- */
    function initGallery(root) {
      var track = root.querySelector('[data-gallery-track]');
      var slides = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-slide]'));
      var dots = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-dot]'));
      var counter = root.querySelector('[data-gallery-counter]');
      var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-thumb]'));
      var rail = root.querySelector('[data-gallery-rail]');
      var stage = root.querySelector('.drawlz-pdp-hero__stage');
      var chevs = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-chev]'));
      if (!slides.length) return;

      var idx = 0;
      /* every index mapping (track scroll position, dot clicks, the counter)
         runs on the VISIBLE subset, never the raw media list. */
      var visible = slides.map(function (s, i) { return i; });
      function visIndex(i) { return visible.indexOf(i); }

      function render() {
        slides.forEach(function (s, i) { s.classList.toggle('is-active', i === idx); });
        thumbs.forEach(function (t, i) { t.setAttribute('aria-current', String(i === idx)); });
        dots.forEach(function (dot, i) { dot.setAttribute('aria-current', String(i === idx)); });
        if (counter) counter.textContent = (visIndex(idx) + 1) + ' / ' + visible.length;
      }

      if (track) track.addEventListener('scroll', function () {
        var i = Math.round(track.scrollLeft / track.clientWidth);
        var g = visible[Math.max(0, Math.min(visible.length - 1, i))];
        if (g != null && g !== idx) { idx = g; render(); }
      }, { passive: true });

      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () {
          var vi = visIndex(i);
          if (vi === -1 || !track) return;
          track.scrollTo({ left: vi * track.clientWidth, behavior: scrollBehavior });
        });
      });

      thumbs.forEach(function (t, i) {
        t.addEventListener('click', function () { idx = i; render(); });
      });

      chevs.forEach(function (chev) {
        chev.addEventListener('click', function () {
          if (!rail) return;
          var step = rail.clientHeight / 5 + 8;
          rail.scrollBy({ top: chev.getAttribute('data-gallery-chev') === 'up' ? -step : step, behavior: scrollBehavior });
        });
      });

      function chevState() {
        if (!rail) return;
        chevs.forEach(function (c) {
          c.disabled = c.getAttribute('data-gallery-chev') === 'up'
            ? rail.scrollTop <= 2
            : rail.scrollTop + rail.clientHeight >= rail.scrollHeight - 2;
        });
      }
      function sizeRail() {
        if (!rail || !stage) return;
        var h = stage.getBoundingClientRect().height;
        if (h > 40) rail.style.maxHeight = Math.round(h) + 'px';
        var over = rail.scrollHeight > rail.clientHeight + 4;
        chevs.forEach(function (c) { c.hidden = !over; });
        if (over) chevState();
      }
      if (rail) {
        rail.addEventListener('scroll', chevState, { passive: true });
        window.addEventListener('resize', sizeRail);
        slides.forEach(function (s) {
          var img = s.querySelector('img');
          if (img && !img.complete) img.addEventListener('load', sizeRail);
        });
        sizeRail();
      }

      /* Which colour does each slide belong to? Resolved ONCE, in three
         tiers, because the two sources disagree and only one is authoritative:

         1. VARIANT IMAGE — the slide is some colour's variant image. This is
            merchandising data the owner set deliberately, so it always wins.
            It is what fixes Blessed Tee: the White variant's image is
            described as "men's black graphic t-shirt", and tier 1 correctly
            reads it as White regardless of that copy.
         2. ALT TEXT — no variant claims this media, so fall back to the
            earliest colour name in its alt. This is a guess, but it is the
            only signal available and it recovers genuine extra shots: the
            two unassigned Blessed detail views name black in their alt and
            belong under Black. Assigning them to a variant in the admin
            promotes them to tier 1 and removes the guesswork.
         3. SHARED — no variant and no colour named. Shown under EVERY
            colour, which is Shopify's own rule for unassigned media, so a
            generic lifestyle or scale shot is never silently lost. */
      var slideColor = slides.map(function (s) {
        var img = s.querySelector('img');
        var b = baseName(img && img.getAttribute('src'));
        var i;
        for (i = 0; i < colors.length; i++) {
          if (b && colorImg[colors[i]][b]) return colors[i];
        }
        var alt = ((img && img.getAttribute('alt')) || '').toLowerCase();
        var best = null, bestAt = -1;
        colors.forEach(function (n) {
          var at = alt.indexOf(n.toLowerCase());
          if (at !== -1 && (bestAt === -1 || at < bestAt)) { best = n; bestAt = at; }
        });
        return best;
      });

      function applyColorFilter(color) {
        if (!color) return;
        var show = slides.map(function (s, i) {
          return slideColor[i] === null || slideColor[i] === color;
        });
        /* a colour with nothing at all would render a blank gallery */
        if (show.indexOf(true) === -1) show = slides.map(function () { return true; });
        visible = [];
        slides.forEach(function (s, i) {
          s.hidden = !show[i];
          if (thumbs[i]) thumbs[i].hidden = !show[i];
          if (dots[i]) dots[i].hidden = !show[i];
          if (show[i]) visible.push(i);
        });
        /* reset to the colour's FIRST image whenever the set changed */
        idx = visible[0];
        render();
        if (track) track.scrollTo({ left: 0, behavior: 'auto' });
        sizeRail();
      }

      render();
      applyToGalleries.push(applyColorFilter);
    }

    /* ---------- sticky bar thumb + price ---------- */
    function updateSticky(color) {
      var src = colorSrc[color];
      if (src) {
        Array.prototype.forEach.call(d.querySelectorAll('[data-drawlz-sticky-thumb]'), function (img) {
          if (img.getAttribute('src') !== src) img.setAttribute('src', src);
        });
      }
      /* price is a nice-to-have for variable-price multi-colour products.
         Skipped while a rotation offer is active, because the rotation
         state machine owns the sticky price in that mode. */
      var pf = colorPrice[color];
      if (pf && !d.body.classList.contains('drawlz-rotation-on')) {
        Array.prototype.forEach.call(d.querySelectorAll('[data-drawlz-sticky-price]'), function (el) {
          if (el.textContent.trim() !== pf) el.textContent = pf;
        });
      }
    }

    /* ---------- colour state: READ ONLY, owned by drawlz-pdp.js ---------- */
    function currentColor() {
      var el = d.querySelector('[data-drawlz-color-swatch][aria-pressed="true"]');
      return el ? el.getAttribute('data-color') : null;
    }

    var last = null;
    function sync() {
      var c = currentColor();
      if (!c || c === last) return;
      last = c;
      applyToGalleries.forEach(function (fn) { fn(c); });
      updateSticky(c);
    }

    onReady(function () {
      roots.forEach(initGallery);
      sync();
      /* drawlz-pdp.js sets aria-pressed on every swatch whenever colour
         state changes — watching that one attribute is how this file stays
         in step without owning any state of its own. */
      if (window.MutationObserver) {
        new MutationObserver(sync).observe(d.body, {
          subtree: true,
          attributes: true,
          attributeFilter: ['aria-pressed']
        });
      }
      /* belt and braces: a direct click path in case a future swatch
         implementation stops round-tripping through aria-pressed. */
      d.addEventListener('click', function (e) {
        if (e.target.closest && e.target.closest('[data-drawlz-color-swatch]')) setTimeout(sync, 0);
      });
    });
  }

  run();
}());
